<script>
// Bespoke, dependency-free acquisition trend chart.
// SVG draws the area + lines (stretched, non-scaling strokes); an HTML overlay
// renders crisp hover guide / dots / tooltip positioned by percentage.
export default {
  name: "TrendChart",
  props: {
    points: { type: Array, default: () => [] }, // [{date, applicants, applications, contracts}]
    height: { type: Number, default: 240 },
  },
  data: () => ({
    hoverIndex: -1,
    W: 1000,
    H: 300,
    padY: 24,
  }),
  computed: {
    // Continuous day series (gap-filled with zeros) for an honest curve.
    series() {
      const raw = this.points || [];
      if (!raw.length) return [];
      const byDate = {};
      raw.forEach((p) => (byDate[p.date] = p));
      const out = [];
      const start = new Date(raw[0].date + "T00:00:00Z");
      const end = new Date(raw[raw.length - 1].date + "T00:00:00Z");
      for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
        const key = d.toISOString().slice(0, 10);
        const p = byDate[key] || {};
        out.push({
          date: key,
          applicants: Number(p.applicants) || 0,
          applications: Number(p.applications) || 0,
          contracts: Number(p.contracts) || 0,
        });
      }
      return out;
    },
    maxY() {
      let m = 0;
      this.series.forEach((p) => {
        m = Math.max(m, p.applicants, p.applications, p.contracts);
      });
      return Math.max(m, 1);
    },
    n() {
      return this.series.length;
    },
    // pixel coords in the fixed W×H space
    coords() {
      const innerH = this.H - this.padY * 2;
      const step = this.n > 1 ? this.W / (this.n - 1) : 0;
      const y = (v) => this.padY + innerH * (1 - v / this.maxY);
      return this.series.map((p, i) => ({
        x: this.n > 1 ? i * step : this.W / 2,
        yApplicants: y(p.applicants),
        yContracts: y(p.contracts),
        p,
      }));
    },
    linePath() {
      if (!this.coords.length) return "";
      return this.coords
        .map((c, i) => `${i === 0 ? "M" : "L"}${c.x.toFixed(1)},${c.yApplicants.toFixed(1)}`)
        .join(" ");
    },
    areaPath() {
      if (!this.coords.length) return "";
      const top = this.coords
        .map((c, i) => `${i === 0 ? "M" : "L"}${c.x.toFixed(1)},${c.yApplicants.toFixed(1)}`)
        .join(" ");
      const last = this.coords[this.coords.length - 1];
      return `${top} L${last.x.toFixed(1)},${this.H - this.padY} L${this.coords[0].x.toFixed(1)},${this.H - this.padY} Z`;
    },
    contractsPath() {
      if (!this.coords.length) return "";
      return this.coords
        .map((c, i) => `${i === 0 ? "M" : "L"}${c.x.toFixed(1)},${c.yContracts.toFixed(1)}`)
        .join(" ");
    },
    gridY() {
      // 4 horizontal gridlines + value labels
      const lines = [];
      for (let i = 0; i <= 3; i++) {
        const frac = i / 3;
        lines.push({
          yPct: frac * 100,
          value: Math.round(this.maxY * (1 - frac)),
        });
      }
      return lines;
    },
    hover() {
      if (this.hoverIndex < 0 || this.hoverIndex >= this.n) return null;
      const p = this.series[this.hoverIndex];
      return {
        ...p,
        xPct: this.n > 1 ? (this.hoverIndex / (this.n - 1)) * 100 : 50,
      };
    },
    firstLabel() {
      return this.n ? this.fmtDate(this.series[0].date) : "";
    },
    lastLabel() {
      return this.n ? this.fmtDate(this.series[this.n - 1].date) : "";
    },
  },
  methods: {
    onMove(e) {
      if (this.n < 1) return;
      const rect = this.$refs.plot.getBoundingClientRect();
      const rel = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
      this.hoverIndex = Math.round(rel * (this.n - 1));
    },
    onLeave() {
      this.hoverIndex = -1;
    },
    yPct(v) {
      const innerFrac = 1 - v / this.maxY;
      // map inner plot (with padY) to overlay percentage
      const innerH = this.H - this.padY * 2;
      const px = this.padY + innerH * innerFrac;
      return (px / this.H) * 100;
    },
    fmtDate(d) {
      const dt = new Date(d + "T00:00:00Z");
      return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    },
  },
};
</script>

<template>
  <div class="tc-wrap" :style="{ height: height + 'px' }">
    <div v-if="!n" class="tc-empty">{{ $t('No data') }}</div>

    <template v-else>
      <!-- gridlines + y labels -->
      <div class="tc-grid">
        <div
          v-for="(g, i) in gridY"
          :key="'g' + i"
          class="tc-grid-line"
          :style="{ top: g.yPct + '%' }"
        >
          <span class="tc-grid-val">{{ g.value }}</span>
        </div>
      </div>

      <div
        ref="plot"
        class="tc-plot"
        @mousemove="onMove"
        @mouseleave="onLeave"
      >
        <svg
          class="tc-svg"
          :viewBox="`0 0 ${W} ${H}`"
          preserveAspectRatio="none"
          width="100%"
          height="100%"
        >
          <defs>
            <linearGradient id="tcArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stop-color="var(--rmx-gold)" stop-opacity="0.42" />
              <stop offset="100%" stop-color="var(--rmx-gold)" stop-opacity="0" />
            </linearGradient>
          </defs>
          <path :d="areaPath" fill="url(#tcArea)" />
          <path
            class="tc-line tc-line--gold"
            :d="linePath"
            fill="none"
            stroke="var(--rmx-gold)"
            stroke-width="2.5"
            vector-effect="non-scaling-stroke"
          />
          <path
            class="tc-line tc-line--emerald"
            :d="contractsPath"
            fill="none"
            stroke="var(--rmx-emerald)"
            stroke-width="2"
            stroke-dasharray="1 0"
            vector-effect="non-scaling-stroke"
          />
        </svg>

        <!-- hover overlay -->
        <div v-if="hover" class="tc-guide" :style="{ left: hover.xPct + '%' }"></div>
        <div
          v-if="hover"
          class="tc-dot tc-dot--gold"
          :style="{ left: hover.xPct + '%', top: yPct(hover.applicants) + '%' }"
        ></div>
        <div
          v-if="hover"
          class="tc-dot tc-dot--emerald"
          :style="{ left: hover.xPct + '%', top: yPct(hover.contracts) + '%' }"
        ></div>

        <div
          v-if="hover"
          class="tc-tip"
          :class="{ 'tc-tip--right': hover.xPct > 60 }"
          :style="{ left: hover.xPct + '%' }"
        >
          <div class="tc-tip-date">{{ fmtDate(hover.date) }}</div>
          <div class="tc-tip-row"><span class="tc-k tc-k--gold"></span>{{ $t('Applicants') }}<b>{{ hover.applicants }}</b></div>
          <div class="tc-tip-row"><span class="tc-k tc-k--blue"></span>{{ $t('Applications') }}<b>{{ hover.applications }}</b></div>
          <div class="tc-tip-row"><span class="tc-k tc-k--emerald"></span>{{ $t('Contracts') }}<b>{{ hover.contracts }}</b></div>
        </div>
      </div>

      <div class="tc-axis">
        <span>{{ firstLabel }}</span>
        <span class="tc-legend">
          <em class="tc-k tc-k--gold"></em>{{ $t('Applicants') }}
          <em class="tc-k tc-k--emerald"></em>{{ $t('Contracts') }}
        </span>
        <span>{{ lastLabel }}</span>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.tc-wrap {
  position: relative;
  width: 100%;
}
.tc-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--rmx-muted);
  font-size: 13px;
}
.tc-grid {
  position: absolute;
  inset: 0 0 28px 0;
  pointer-events: none;
}
.tc-grid-line {
  position: absolute;
  left: 0;
  right: 0;
  border-top: 1px dashed var(--rmx-line);
}
.tc-grid-val {
  position: absolute;
  left: 0;
  top: -8px;
  font-size: 10px;
  font-weight: 700;
  color: var(--rmx-muted);
  font-variant-numeric: tabular-nums;
  background: var(--rmx-panel);
  padding-right: 4px;
}
.tc-plot {
  position: absolute;
  inset: 0 0 28px 0;
  cursor: crosshair;
}
.tc-svg {
  display: block;
  overflow: visible;
}
.tc-line {
  stroke-linejoin: round;
  stroke-linecap: round;
  animation: tcDraw 1.1s ease forwards;
  stroke-dasharray: 1400;
  stroke-dashoffset: 1400;
}
.tc-line--emerald {
  animation-delay: 0.15s;
}
@keyframes tcDraw {
  to {
    stroke-dashoffset: 0;
  }
}
.tc-guide {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: var(--rmx-line-strong);
  transform: translateX(-0.5px);
}
.tc-dot {
  position: absolute;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  border: 2px solid var(--rmx-panel);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06);
}
.tc-dot--gold {
  background: var(--rmx-gold);
}
.tc-dot--emerald {
  background: var(--rmx-emerald);
}
.tc-tip {
  position: absolute;
  top: 6px;
  transform: translateX(-50%);
  background: var(--rmx-tip-bg);
  border: 1px solid var(--rmx-line);
  border-radius: 12px;
  padding: 8px 11px;
  min-width: 128px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  pointer-events: none;
  z-index: 3;
}
.tc-tip--right {
  transform: translateX(-100%) translateX(-12px);
}
.tc-tip-date {
  font-size: 11px;
  font-weight: 800;
  color: var(--rmx-text);
  margin-bottom: 5px;
  letter-spacing: 0.3px;
}
.tc-tip-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: var(--rmx-muted);
  margin-top: 3px;
}
.tc-tip-row b {
  margin-left: auto;
  color: var(--rmx-text);
  font-variant-numeric: tabular-nums;
}
.tc-k {
  width: 9px;
  height: 9px;
  border-radius: 3px;
  display: inline-block;
}
.tc-k--gold {
  background: var(--rmx-gold);
}
.tc-k--emerald {
  background: var(--rmx-emerald);
}
.tc-k--blue {
  background: #38bdf8;
}
.tc-axis {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  color: var(--rmx-muted);
  font-weight: 600;
}
.tc-legend {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.tc-legend em {
  margin-left: 8px;
}
</style>
